/**
 * Checklist Items Editor
 *
 * Component for managing checklist items in a template.
 * Supports adding, editing, reordering, and deleting items.
 */

import { useState } from 'react';
import {
  Plus,
  GripVertical,
  Trash2,
  Edit,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  Square,
  Type,
  Hash,
  Calendar,
  Camera,
  PenLine,
  List,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import type { ChecklistItem, ChecklistItemType } from '../types';

type TemplateChecklistItem = Omit<
  ChecklistItem,
  'id' | 'value' | 'completedAt' | 'completedByName' | 'notes' | 'photoFileId' | 'signatureFileId'
>;

interface ChecklistItemsEditorProps {
  items: TemplateChecklistItem[];
  onChange: (items: TemplateChecklistItem[]) => void;
}

const ITEM_TYPES: { value: ChecklistItemType; label: string; icon: React.ReactNode }[] = [
  { value: 'checkbox', label: 'Checkbox', icon: <Square className="h-4 w-4" /> },
  { value: 'text', label: 'Text Input', icon: <Type className="h-4 w-4" /> },
  { value: 'number', label: 'Number Input', icon: <Hash className="h-4 w-4" /> },
  { value: 'date', label: 'Date Picker', icon: <Calendar className="h-4 w-4" /> },
  { value: 'photo', label: 'Photo Upload', icon: <Camera className="h-4 w-4" /> },
  { value: 'signature', label: 'Signature', icon: <PenLine className="h-4 w-4" /> },
  { value: 'dropdown', label: 'Dropdown', icon: <List className="h-4 w-4" /> },
];

const getTypeIcon = (type: ChecklistItemType) => {
  const typeInfo = ITEM_TYPES.find((t) => t.value === type);
  return typeInfo?.icon || <Square className="h-4 w-4" />;
};

const generateKey = (displayName: string): string => {
  return displayName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .substring(0, 50);
};

export function ChecklistItemsEditor({ items, onChange }: ChecklistItemsEditorProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [newItem, setNewItem] = useState<Partial<TemplateChecklistItem> | null>(null);

  const handleAddItem = () => {
    setNewItem({
      key: '',
      displayName: '',
      type: 'checkbox',
      required: false,
      sortOrder: items.length,
    });
    setEditingIndex(null);
  };

  const handleSaveNewItem = () => {
    if (!newItem || !newItem.displayName?.trim()) return;

    const key = newItem.key?.trim() || generateKey(newItem.displayName);

    const item: TemplateChecklistItem = {
      key,
      displayName: newItem.displayName.trim(),
      type: newItem.type || 'checkbox',
      required: newItem.required || false,
      sortOrder: items.length,
      options: newItem.options,
    };

    onChange([...items, item]);
    setNewItem(null);
  };

  const handleCancelNewItem = () => {
    setNewItem(null);
  };

  const handleUpdateItem = (index: number, updates: Partial<TemplateChecklistItem>) => {
    const updated = [...items];
    updated[index] = { ...updated[index], ...updates };
    onChange(updated);
  };

  const handleDeleteItem = (index: number) => {
    const updated = items.filter((_, i) => i !== index);
    // Update sort orders
    updated.forEach((item, i) => {
      item.sortOrder = i;
    });
    onChange(updated);
    if (editingIndex === index) setEditingIndex(null);
    if (expandedIndex === index) setExpandedIndex(null);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const updated = [...items];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    updated.forEach((item, i) => {
      item.sortOrder = i;
    });
    onChange(updated);
    setExpandedIndex(index - 1);
  };

  const handleMoveDown = (index: number) => {
    if (index === items.length - 1) return;
    const updated = [...items];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    updated.forEach((item, i) => {
      item.sortOrder = i;
    });
    onChange(updated);
    setExpandedIndex(index + 1);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Define the checklist items technicians will complete during installation.
        </p>
        <Button size="sm" onClick={handleAddItem} disabled={!!newItem}>
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </Button>
      </div>

      {/* Items List */}
      <div className="space-y-2">
        {items.map((item, index) => (
          <Collapsible
            key={`${item.key}-${index}`}
            open={expandedIndex === index}
            onOpenChange={(open) => setExpandedIndex(open ? index : null)}
          >
            <Card>
              <div className="flex items-center gap-2 p-3">
                <div className="flex flex-col gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5"
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                  >
                    <ChevronUp className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5"
                    onClick={() => handleMoveDown(index)}
                    disabled={index === items.length - 1}
                  >
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </div>

                <GripVertical className="h-4 w-4 text-muted-foreground" />

                <div className="flex-1 flex items-center gap-3">
                  <div className="text-muted-foreground">{getTypeIcon(item.type)}</div>
                  <div className="flex-1">
                    <div className="font-medium">{item.displayName}</div>
                    <div className="text-xs text-muted-foreground">Key: {item.key}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.required && (
                      <Badge variant="secondary" className="text-xs">
                        Required
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-xs">
                      {ITEM_TYPES.find((t) => t.value === item.type)?.label}
                    </Badge>
                  </div>
                </div>

                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Edit className="h-4 w-4" />
                  </Button>
                </CollapsibleTrigger>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => handleDeleteItem(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              <CollapsibleContent>
                <CardContent className="pt-0 pb-4">
                  <div className="grid gap-4 md:grid-cols-2 border-t pt-4">
                    <div className="space-y-2">
                      <Label>Display Name</Label>
                      <Input
                        value={item.displayName}
                        onChange={(e) =>
                          handleUpdateItem(index, { displayName: e.target.value })
                        }
                        placeholder="e.g., Verify sensor mounting"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Key (unique identifier)</Label>
                      <Input
                        value={item.key}
                        onChange={(e) => handleUpdateItem(index, { key: e.target.value })}
                        placeholder="e.g., verify_mounting"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select
                        value={item.type}
                        onValueChange={(value: ChecklistItemType) =>
                          handleUpdateItem(index, { type: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ITEM_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              <div className="flex items-center gap-2">
                                {type.icon}
                                {type.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center justify-between rounded-lg border p-3">
                      <Label htmlFor={`required-${index}`}>Required</Label>
                      <Switch
                        id={`required-${index}`}
                        checked={item.required}
                        onCheckedChange={(checked) =>
                          handleUpdateItem(index, { required: checked })
                        }
                      />
                    </div>

                    {/* Dropdown options editor */}
                    {item.type === 'dropdown' && (
                      <div className="md:col-span-2 space-y-2">
                        <Label>Dropdown Options (comma-separated)</Label>
                        <Input
                          value={
                            (item.options?.choices as string[])?.join(', ') || ''
                          }
                          onChange={(e) =>
                            handleUpdateItem(index, {
                              options: {
                                ...item.options,
                                choices: e.target.value
                                  .split(',')
                                  .map((s) => s.trim())
                                  .filter(Boolean),
                              },
                            })
                          }
                          placeholder="Option 1, Option 2, Option 3"
                        />
                      </div>
                    )}

                    {/* Number options */}
                    {item.type === 'number' && (
                      <div className="md:col-span-2 grid gap-4 md:grid-cols-3">
                        <div className="space-y-2">
                          <Label>Min Value</Label>
                          <Input
                            type="number"
                            value={(item.options?.min as number) ?? ''}
                            onChange={(e) =>
                              handleUpdateItem(index, {
                                options: {
                                  ...item.options,
                                  min: e.target.value ? Number(e.target.value) : undefined,
                                },
                              })
                            }
                            placeholder="No minimum"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Max Value</Label>
                          <Input
                            type="number"
                            value={(item.options?.max as number) ?? ''}
                            onChange={(e) =>
                              handleUpdateItem(index, {
                                options: {
                                  ...item.options,
                                  max: e.target.value ? Number(e.target.value) : undefined,
                                },
                              })
                            }
                            placeholder="No maximum"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Unit</Label>
                          <Input
                            value={(item.options?.unit as string) ?? ''}
                            onChange={(e) =>
                              handleUpdateItem(index, {
                                options: {
                                  ...item.options,
                                  unit: e.target.value || undefined,
                                },
                              })
                            }
                            placeholder="e.g., cm, kg, %"
                          />
                        </div>
                      </div>
                    )}

                    {/* Text options */}
                    {item.type === 'text' && (
                      <div className="md:col-span-2 space-y-2">
                        <Label>Placeholder Text</Label>
                        <Input
                          value={(item.options?.placeholder as string) ?? ''}
                          onChange={(e) =>
                            handleUpdateItem(index, {
                              options: {
                                ...item.options,
                                placeholder: e.target.value || undefined,
                              },
                            })
                          }
                          placeholder="Enter placeholder text..."
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        ))}

        {/* New Item Form */}
        {newItem && (
          <Card className="border-primary">
            <CardContent className="pt-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Display Name *</Label>
                  <Input
                    value={newItem.displayName || ''}
                    onChange={(e) => {
                      const newDisplayName = e.target.value;
                      setNewItem((prev) => {
                        if (!prev) return prev;
                        const isKeyAutoGenerated =
                          !prev.key || prev.key === generateKey(prev.displayName || '');
                        return {
                          ...prev,
                          displayName: newDisplayName,
                          key: isKeyAutoGenerated ? generateKey(newDisplayName) : prev.key,
                        };
                      });
                    }}
                    placeholder="e.g., Verify sensor mounting"
                    autoFocus
                  />
                </div>

                <div className="space-y-2">
                  <Label>Key (auto-generated if empty)</Label>
                  <Input
                    value={newItem.key || ''}
                    onChange={(e) => {
                      const newKey = e.target.value;
                      setNewItem((prev) => (prev ? { ...prev, key: newKey } : prev));
                    }}
                    placeholder="e.g., verify_mounting"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={newItem.type || 'checkbox'}
                    onValueChange={(value: ChecklistItemType) =>
                      setNewItem((prev) => (prev ? { ...prev, type: value } : prev))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ITEM_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            {type.icon}
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between rounded-lg border p-3">
                  <Label htmlFor="new-required">Required</Label>
                  <Switch
                    id="new-required"
                    checked={newItem.required || false}
                    onCheckedChange={(checked) =>
                      setNewItem((prev) => (prev ? { ...prev, required: checked } : prev))
                    }
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={handleCancelNewItem}>
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveNewItem}
                  disabled={!newItem.displayName?.trim()}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Add Item
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {items.length === 0 && !newItem && (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-8 text-center">
              <p className="text-muted-foreground mb-4">
                No checklist items yet. Add items that technicians will complete during
                installation.
              </p>
              <Button variant="outline" onClick={handleAddItem}>
                <Plus className="h-4 w-4 mr-2" />
                Add First Item
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
